/**
 * Configurações → Etapas do funil.
 *
 * ── Por que esta tela existe ──────────────────────────────────────────────
 *
 * A A01 §4 mediu o funil que vem no motor de CRM — `NEW`, `SCREENING`,
 * `MEETING`, `PROPOSAL`, `CUSTOMER` — e concluiu: *"o funil padrão do Twenty
 * não é o funil de uma revenda de veículos"*. Sem esta tela, toda revenda
 * trabalha no funil de outra pessoa.
 *
 * ── O que a tela deixa e o que NÃO deixa fazer ────────────────────────────
 *
 * Acrescentar no fim, renomear, trocar a cor, reordenar, e remover etapa
 * VAZIA. O que ela não deixa é mudar o valor de uma etapa que já existe — e o
 * motivo é o mesmo que a API dá: o valor é o que está gravado em cada
 * oportunidade, e trocá-lo faria todas as daquela etapa sumirem do quadro sem
 * erro nenhum.
 *
 * Remover etapa com negócios dentro é recusado pela API, com a contagem. A
 * tela repete o aviso ANTES do clique, porque descobrir depois custa uma
 * viagem e um susto.
 *
 * ── Mais de um funil, e por que na MESMA seção ────────────────────────────
 *
 * Cada funil tem etapas próprias — "Vendas" e "Oficina" não compartilham
 * coluna nenhuma. Então escolher o funil não é um assunto separado de
 * configurar as etapas: é a pergunta que vem ANTES dela, e toda ação daqui
 * para baixo acontece dentro do funil escolhido.
 *
 * Uma seção só para "funis" e outra para "etapas" forçaria ir e voltar para
 * fazer uma coisa só — e deixaria a lista de etapas sem dizer de quem ela é,
 * que é o jeito mais barato de alguém renomear a coluna do funil errado.
 *
 * ── Criar funil pede a primeira etapa junto ───────────────────────────────
 *
 * Porque não existe funil vazio: ele é o conjunto das etapas com o prefixo
 * dele (ver `packages/modulos/comercial/src/dominio/funil.ts`). Um sem etapa nenhuma não teria quadro para
 * mostrar nem para onde arrastar — seria um estado que a tela teria de
 * explicar e alguém teria de limpar depois.
 */

import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CORES_DE_ETAPA, type EtapaDoFunil } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoSelecao, Cartao, Chip, Segmentado } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { HEX_DA_COR, nomeDaEtapa } from '@/entities/comercial/etapa';
import { ordemComEtapaInserida, ordemComTroca } from '../lib/ordem-das-etapas';
import { RoteamentoDeFunil } from './roteamento-de-funil';
import { MotivosDePerdaDoFunil } from './motivos-de-perda';

/** De trabalho, ou final com resultado; `final` é a antiga, sem classificação. */
type TipoDaEtapa = 'aberta' | 'ganho' | 'perdido' | 'final';

/** O que o motor chama de cor, em português, para a tela. */
const NOME_DA_COR: Record<string, string> = {
  green: 'verde',
  turquoise: 'turquesa',
  sky: 'azul-claro',
  blue: 'azul',
  purple: 'roxo',
  pink: 'rosa',
  red: 'vermelho',
  orange: 'laranja',
  yellow: 'amarelo',
  gray: 'cinza',
};

/**
 * O que dizer nos códigos cujo detalhe a API não deixa sair.
 *
 * `ENGINE_INDISPONIVEL` carrega texto do motor — endereço interno, nome de
 * serviço —, então a API o retém e quem escreve a frase é esta tela.
 */
const POR_CODIGO: Readonly<Record<string, string>> = {
  ENGINE_INDISPONIVEL: 'Não foi possível salvar as etapas agora. Nada mudou, tente daqui a pouco.',
  SEM_PERMISSAO:
    'Mudar etapa muda o funil da organização inteira, e isso é de quem administra a organização.',
};

export function SecaoEtapasDoFunil(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();

  const etapas = useQuery({
    queryKey: chaves.etapasDoFunil(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.etapasDoFunil({ sinal: signal }),
  });

  /*
   * Os funis saem de uma consulta PRÓPRIA, e não das etapas.
   *
   * As duas listas derivam do mesmo campo do motor, então agrupar aqui daria a
   * mesma coisa — menos o NOME. O nome é escolhido pela revenda e guardado do
   * nosso lado; a etapa não o carrega, e derivá-lo da chave devolveria
   * "Pos venda" para quem digitou "Pós-venda".
   */
  const funis = useQuery({
    queryKey: chaves.funis(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.funisDaOrganizacao({ sinal: signal }),
  });

  /*
   * `null` é "ainda não escolhi", e não "nenhum".
   *
   * A lista chega depois da primeira pintura, então um estado iniciado com a
   * primeira chave nasceria vazio e nunca se corrigiria. Quem resolve é o
   * `escolhido` logo abaixo, que cai na primeira da lista enquanto ninguém
   * tiver mexido no seletor.
   */
  const [funilEscolhido, definirFunilEscolhido] = useState<string | null>(null);

  /*
   * Duas abas: ORGANIZAR o que existe, e CRIAR o que falta.
   *
   * Tudo numa página só misturava as duas intenções: o formulário de funil
   * novo abria no meio da lista de etapas, e a lista mostrava um funil por vez
   * atrás de um seletor. Separadas, "Funis e etapas" mostra todos os funis com
   * as etapas deles e é onde se ordena; "Criar" só cria.
   */
  /*
   * A terceira aba, "Roteamento", diz para qual funil vai cada número: é a
   * mesma pergunta de "qual funil", vista pela porta de entrada.
   */
  const [aba, definirAba] = useState<'organizar' | 'criar' | 'roteamento' | 'motivos'>('organizar');
  const [nomeDoFunilNovo, definirNomeDoFunilNovo] = useState('');
  const [rotuloDaPrimeira, definirRotuloDaPrimeira] = useState('');
  const [corDaPrimeira, definirCorDaPrimeira] = useState<string>('blue');
  /** Qual funil está com o nome aberto para edição. */
  const [renomeandoFunil, definirRenomeandoFunil] = useState<string | null>(null);
  const [nomeRenomeado, definirNomeRenomeado] = useState('');

  const [rotulo, definirRotulo] = useState('');
  const [cor, definirCor] = useState<string>('blue');
  /** Onde a etapa nova entra. `-1` = no fim, que é o padrão. */
  const [posicao, definirPosicao] = useState<number>(-1);

  /* Qual etapa está aberta para edição, e o que já foi digitado nela. */
  const [editando, definirEditando] = useState<string | null>(null);
  const [rotuloEditado, definirRotuloEditado] = useState('');
  const [corEditada, definirCorEditada] = useState<string>('blue');
  /*
   * O TIPO da etapa, e não mais só "é final" (057).
   *
   * `final` é a etapa que já era final antes de haver ganho e perdido, e que
   * ninguém classificou. Ela aparece como opção só nela mesma, para salvar uma
   * troca de cor não a classificar sem ninguém pedir.
   */
  const [tipoEditado, definirTipoEditado] = useState<TipoDaEtapa>('aberta');
  /**
   * O tipo da etapa nova. Era só na edição, e a etapa final criada sem ele
   * ficava "de trabalho" em silêncio: o card ganho voltava a andar, e a perda
   * com motivo era recusada.
   */
  const [tipoNovo, definirTipoNovo] = useState<Exclude<TipoDaEtapa, 'final'>>('aberta');
  /** A etapa nasceu, mas o tipo não gravou: o aviso diz como terminar. */
  const [tipoNaoGravado, definirTipoNaoGravado] = useState<string | null>(null);
  /** O nome da última etapa criada, para confirmar na tela que ela entrou. */
  const [ultimaCriada, definirUltimaCriada] = useState<string | null>(null);

  /*
   * O valor é DERIVADO do rótulo, e mostrado antes de criar.
   *
   * Pedir os dois faria a pessoa escolher um código que ela nunca mais vê. E
   * escondê-lo seria pior: o valor é imutável depois, então ela precisa saber
   * o que está fixando — está ali, em cinza, ao lado do campo.
   */
  const valorDerivado = paraValor(rotulo);

  const invalidar = (): void => {
    void cache.invalidateQueries({ queryKey: chaves.etapasDoFunil() });
    // O quadro desenha as colunas com isto: sem invalidar, a etapa nova só
    // apareceria lá depois de alguém recarregar a página.
    void cache.invalidateQueries({ queryKey: chaves.funil() });
    /*
     * E a lista de funis, que conta as etapas de cada um.
     *
     * Ela também é o que decide se um funil EXISTE: remover a última etapa de
     * "Oficina" apaga o funil, e sem isto o seletor continuaria oferecendo um
     * quadro que a API já responde com 404.
     */
    void cache.invalidateQueries({ queryKey: chaves.funis() });
  };

  /*
   * Criar e, se for o caso, POSICIONAR — dois passos, e de propósito.
   *
   * O motor acrescenta no fim: a posição é o que ordena o quadro, e inserir no
   * meio pela mesma chamada mudaria a ordem das seguintes dentro de uma escrita
   * que a pessoa pediu para "criar". São duas intenções, e a segunda já tinha
   * função própria — é a mesma que as setas usam.
   *
   * Se o reordenar falhar, a etapa EXISTE, no fim, e a mensagem diz isso. É
   * bem melhor que desfazer a criação: nada se perde, e arrastar com as setas
   * conserta em dois cliques.
   */
  const criar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      const criadas = await api.criarEtapaDoFunil({
        corpo: {
          valor: valorDerivado,
          rotulo: rotulo.trim(),
          cor: cor as never,
          /*
           * O valor vai SEM prefixo, e quem compõe `F_VENDAS_NOVO` é o
           * servidor. Omitir o funil é o `padrao` — o caso da revenda que nunca
           * criou outro, e o da que não tem etapa nenhuma ainda.
           */
          ...(escolhido !== null ? { funil: escolhido } : {}),
        },
      });

      /*
       * Qual das etapas de volta é a nova — perguntando à resposta, e não
       * montando o valor completo por aqui. Ver `semPrefixoDoFunil`: a
       * composição é do servidor, e repeti-la seria a segunda regra.
       */
      const nova = criadas.itens.find((e) => !todas.some((a) => a.valor === e.valor));
      if (!nova) return criadas;

      // Ganho e perdido pela mesma chamada da edição: o criar só sabe "de trabalho".
      // Se esta segunda escrita falhar, a etapa EXISTE, de trabalho: o aviso diz isso
      // e como terminar, em vez de "não foi possível criar" sobre algo que foi criado.
      definirTipoNaoGravado(null);
      if (tipoNovo !== 'aberta') {
        try {
          await api.renomearEtapaDoFunil({
            caminho: { valor: nova.valor },
            corpo: { rotulo: rotulo.trim(), cor: cor as never, aberta: false, resultado: tipoNovo },
          });
        } catch {
          definirTipoNaoGravado(rotulo.trim());
        }
      }

      const atual = criadas.itens.map((e) => e.valor);
      const ordem = ordemComEtapaInserida(atual, nova.valor, alvoDaInsercao(itens, posicao));

      // A ordem que já está certa não se manda: seria uma escrita no CRM de
      // todo mundo para não mudar nada.
      if (ordem.every((valor, i) => valor === atual[i])) return criadas;
      return api.reordenarEtapasDoFunil({ corpo: { ordem: [...ordem] } });
    },
    onSuccess: () => {
      definirUltimaCriada(rotulo.trim());
      definirRotulo('');
      definirPosicao(-1);
      definirTipoNovo('aberta');
      invalidar();
    },
  });

  const remover = useMutation({
    mutationFn: async (valor: string) => {
      if (!api) throw new Error('Sem sessão.');
      return api.removerEtapaDoFunil({ caminho: { valor } });
    },
    onSuccess: invalidar,
  });

  /*
   * Renomear existia na API desde a D04 e nunca teve tela.
   *
   * O `valor` NÃO muda — é ele que está gravado em cada oportunidade, e
   * trocá-lo levaria junto todo negócio que estivesse na etapa. O que se edita
   * é o RÓTULO e a COR, que são o que a pessoa lê.
   */
  const renomear = useMutation({
    mutationFn: async (mudanca: {
      valor: string;
      rotulo: string;
      cor: string;
      tipo: TipoDaEtapa;
    }) => {
      if (!api) throw new Error('Sem sessão.');
      return api.renomearEtapaDoFunil({
        caminho: { valor: mudanca.valor },
        corpo: {
          rotulo: mudanca.rotulo,
          cor: mudanca.cor as never,
          aberta: mudanca.tipo === 'aberta',
          // Ganho e perdido vão explícitos. `final` é a sem classificação: não
          // se mexe no resultado, e a aberta perde o seu no servidor.
          ...(mudanca.tipo === 'ganho' || mudanca.tipo === 'perdido'
            ? { resultado: mudanca.tipo }
            : {}),
        },
      });
    },
    onSuccess: () => {
      definirEditando(null);
      invalidar();
    },
  });

  const mover = useMutation({
    mutationFn: async (ordem: readonly string[]) => {
      if (!api) throw new Error('Sem sessão.');
      return api.reordenarEtapasDoFunil({ corpo: { ordem: [...ordem] } });
    },
    onSuccess: invalidar,
  });

  /*
   * Criar funil e criar a primeira etapa dele são UMA chamada.
   *
   * Não é economia de requisição: é o que impede o meio-caminho. Funil sem
   * etapa não existe do lado do motor — o funil É o prefixo das etapas —, então
   * um "criar funil" que falhasse na etapa deixaria um nome guardado apontando
   * para nada, e a lista mostraria um funil cujo quadro responde 404.
   */
  const criarOFunil = useMutation({
    mutationFn: async (entrada: {
      chave: string;
      nome: string;
      valor: string;
      rotulo: string;
      cor: string;
    }) => {
      if (!api) throw new Error('Sem sessão.');
      return api.criarFunil({
        corpo: {
          chave: entrada.chave,
          nome: entrada.nome,
          primeira_etapa: {
            valor: entrada.valor,
            rotulo: entrada.rotulo,
            cor: entrada.cor as never,
          },
        },
      });
    },
    onSuccess: (criado) => {
      // Já mostra o funil recém-criado: quem acabou de criá-lo vai configurar
      // as etapas dele em seguida, e ter de escolhê-lo à mão seria um passo
      // que a tela sabe fazer.
      definirFunilEscolhido(criado.chave);
      definirNomeDoFunilNovo('');
      definirRotuloDaPrimeira('');
      invalidar();
    },
  });

  const renomearOFunil = useMutation({
    mutationFn: async (mudanca: { chave: string; nome: string }) => {
      if (!api) throw new Error('Sem sessão.');
      return api.renomearFunil({
        caminho: { chave: mudanca.chave },
        corpo: { nome: mudanca.nome },
      });
    },
    onSuccess: () => {
      definirRenomeandoFunil(null);
      invalidar();
    },
  });

  const listaDeFunis = funis.data?.itens ?? [];
  /*
   * O funil em que tudo acontece. A primeira da lista enquanto ninguém
   * escolher; `null` só na revenda cujo CRM não declara etapa nenhuma, onde
   * não há funil para escolher.
   */
  const escolhido = funilEscolhido ?? listaDeFunis[0]?.chave ?? null;
  const funilAtual = listaDeFunis.find((f) => f.chave === escolhido) ?? null;

  /* Todas as etapas do workspace — é sobre ESTA lista que a ordem é mandada. */
  const todas = etapas.data?.itens ?? [];
  const itens = escolhido === null ? todas : todas.filter((e) => e.funil === escolhido);

  // Em QUALQUER funil: a aba de organizar mostra todos de uma vez.
  const emEdicao = todas.find((e) => e.valor === editando) ?? null;
  /*
   * Repetido é repetido no WORKSPACE, e não só neste funil.
   *
   * O valor completo carrega o prefixo, então `F_VENDAS_NOVO` e `F_OFICINA_NOVO`
   * convivem — mas quem confere é o servidor, que conhece a composição. Aqui a
   * comparação é contra o valor SEM prefixo das etapas deste funil, que é o que
   * a pessoa digitou.
   */
  const jaExiste = itens.some((e) => semPrefixoDoFunil(e) === valorDerivado);

  /*
   * A mesma regra do servidor, aplicada AQUI.
   *
   * Sem isto a tela mandava uma chave que o motor recusa e mostrava
   * "Requisição inválida" — sobre um campo que a pessoa nem sabia existir.
   * Recusar antes, com o motivo, é a diferença entre "arruma o nome" e "o
   * produto quebrou".
   */
  const valorInvalido = valorDerivado.length >= 2 && !VALOR_VALIDO.test(valorDerivado);
  const podeCriar = valorDerivado.length >= 2 && !jaExiste && !valorInvalido && !criar.isPending;

  /* O mesmo par derivado do funil novo: a chave e o valor da primeira etapa. */
  const chaveDoFunilNovo = paraChaveDeFunil(nomeDoFunilNovo);
  const valorDaPrimeira = paraValor(rotuloDaPrimeira);
  const funilRepetido =
    nomeDoFunilNovo.trim() !== '' &&
    listaDeFunis.some((f) => nomeComparavel(f.nome) === nomeComparavel(nomeDoFunilNovo));
  const podeCriarFunil =
    chaveDoFunilNovo.length >= 2 &&
    VALOR_VALIDO.test(chaveDoFunilNovo) &&
    !funilRepetido &&
    valorDaPrimeira.length >= 2 &&
    VALOR_VALIDO.test(valorDaPrimeira) &&
    !criarOFunil.isPending;

  /* Cada funil com as etapas dele, na ordem do quadro. */
  const grupos = listaDeFunis.map((f) => ({
    funil: f,
    etapas: todas.filter((e) => e.funil === f.chave),
  }));

  /*
   * A linha de uma etapa, com as ações de ORDENAR e de editar.
   *
   * `doFunil` é a lista do funil desta linha, e não a do funil escolhido:
   * a aba de organizar mostra todos os funis, e as setas trocam a etapa com a
   * vizinha DENTRO do funil dela. A ordem que vai para a API continua sendo a
   * do workspace inteiro (`trocarNaOrdem` recebe `todas`), porque é ela que o
   * motor ordena.
   */
  function linhaDaEtapa(
    etapa: EtapaDoFunil,
    i: number,
    doFunil: readonly EtapaDoFunil[],
  ): ReactNode {
    const nome = nomeDaEtapa(etapa.rotulo);
    return (
      /*
        A lista é uma SEQUÊNCIA, e é isso que a bolinha de cor não dizia.
        O número entra em disco, na cor da etapa, e a cor vira lombada da
        linha: é ela que liga esta linha à coluna do quadro.
      */
      <Cartao
        key={etapa.valor}
        como="li"
        variante="linha"
        style={{ '--cor': HEX_DA_COR[etapa.cor] ?? 'var(--texto-3)' } as CSSProperties}
      >
        <span className="admin__etapa-ordem" aria-hidden="true">
          {i + 1}
        </span>
        <span className="admin__etapa-texto">
          {/*
            O nome como o QUADRO o mostra, e sem o código embaixo: as cinco
            etapas de fábrica chegam do motor em inglês, e o código era o valor
            gravado na oportunidade, que quem administra não usa para nada.
          */}
          <span className="admin__etapa-rotulo">{nome}</span>
          {/*
            O selo DENTRO da coluna de texto, e não ao lado dela: em duas
            colunas o cartão tem ~340px, e o selo disputando a fileira com o
            número, o nome e quatro botões espremia o texto.
          */}
          {!etapa.aberta && (
            <span className="admin__etapa-final-selo">
              {etapa.resultado === 'ganho' ? (
                <Chip title="Etapa final: a venda foi ganha">ganho</Chip>
              ) : etapa.resultado === 'perdido' ? (
                <Chip tom="erro" title="Etapa final: a venda foi perdida">
                  perdido
                </Chip>
              ) : (
                <Chip
                  tom="atencao"
                  title="Etapa final sem resultado: edite e diga se é venda ganha ou perdida"
                >
                  final, sem resultado
                </Chip>
              )}
            </span>
          )}
        </span>

        {/* O único grupo que nasce invisível: é ação de LINHA, e aparece com o mouse nela. */}
        <span className="admin__etapa-acoes">
          <Botao
            miudo
            desabilitado={i === 0 || mover.isPending}
            motivo={i === 0 ? 'Já é a primeira etapa.' : 'Movendo…'}
            aria-label={`Mover ${nome} para antes`}
            onClick={() => mover.mutate(trocarNaOrdem(todas, doFunil, i, i - 1))}
          >
            ↑
          </Botao>
          <Botao
            miudo
            desabilitado={i === doFunil.length - 1 || mover.isPending}
            motivo={i === doFunil.length - 1 ? 'Já é a última etapa.' : 'Movendo…'}
            aria-label={`Mover ${nome} para depois`}
            onClick={() => mover.mutate(trocarNaOrdem(todas, doFunil, i, i + 1))}
          >
            ↓
          </Botao>
          <Botao
            miudo
            desabilitado={renomear.isPending}
            motivo="Salvando…"
            aria-label={`Editar ${nome}`}
            onClick={() => {
              definirEditando(etapa.valor);
              definirRotuloEditado(nome);
              definirCorEditada(etapa.cor);
              definirTipoEditado(etapa.aberta ? 'aberta' : (etapa.resultado ?? 'final'));
            }}
          >
            Editar
          </Botao>
          <Botao
            miudo
            desabilitado={doFunil.length <= 1 || remover.isPending}
            motivo={
              doFunil.length <= 1
                ? // Remover a ÚLTIMA etapa apagaria o funil: ele é o conjunto das
                  // etapas com o prefixo dele, e sem nenhuma o quadro responde 404.
                  'O funil precisa de pelo menos uma etapa.'
                : 'Removendo…'
            }
            aria-label={`Remover ${nome}`}
            onClick={() => remover.mutate(etapa.valor)}
          >
            Remover
          </Botao>
        </span>
      </Cartao>
    );
  }

  /*
   * A edição abre NO LUGAR, embaixo da lista do funil dela, e não num diálogo.
   * A lista é a referência: quem renomeia "Proposta" quer ver as vizinhas para
   * escolher um nome que não se confunda com elas.
   */
  function formularioDeEdicao(etapa: EtapaDoFunil): ReactNode {
    return (
      <Cartao
        como="form"
        variante="folgado"
        parte="admin__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (rotuloEditado.trim() !== '') {
            renomear.mutate({
              valor: etapa.valor,
              rotulo: rotuloEditado.trim(),
              cor: corEditada,
              tipo: tipoEditado,
            });
          }
        }}
      >
        <Campo
          id="editar-etapa-nome"
          rotulo={`Nome de "${nomeDaEtapa(etapa.rotulo)}"`}
          type="text"
          value={rotuloEditado}
          maxLength={40}
          autoFocus
          ajuda="Só o nome muda. As oportunidades que estão nesta etapa continuam nela."
          onChange={(e) => definirRotuloEditado(e.currentTarget.value)}
        />

        <CampoSelecao
          rotulo="Cor"
          value={corEditada}
          onChange={(e) => definirCorEditada(e.currentTarget.value)}
        >
          {CORES_DE_ETAPA.map((c) => (
            <option key={c} value={c}>
              {NOME_DA_COR[c] ?? c}
            </option>
          ))}
        </CampoSelecao>

        {/*
          De trabalho, ganho ou perdido. Final encerra a oportunidade: dela não
          se arrasta para fora, porque reabrir é decisão, não gesto. Ganho e
          perdido são o que permite medir conversão (057).
        */}
        <CampoSelecao
          rotulo="Tipo da etapa"
          value={tipoEditado}
          disabled={renomear.isPending}
          onChange={(e) => definirTipoEditado(e.currentTarget.value as TipoDaEtapa)}
        >
          <option value="aberta">De trabalho</option>
          <option value="ganho">Final: venda ganha</option>
          <option value="perdido">Final: venda perdida</option>
          {/* `!resultado`, e não `=== null`: a API anterior à 057 não manda o
              campo, e na subida a tela nova pode ler a API velha. */}
          {!etapa.aberta && !etapa.resultado && <option value="final">Final, sem resultado</option>}
        </CampoSelecao>
        <TiposDaEtapa escolhido={tipoEditado} />

        {renomear.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(renomear.error, {
              generica: 'Não foi possível salvar a etapa.',
              porCodigo: POR_CODIGO,
            })}
          </Faixa>
        )}

        <span className="admin__form-acoes">
          <Botao
            type="submit"
            variante="primario"
            desabilitado={rotuloEditado.trim() === '' || renomear.isPending}
            motivo="Escreva o nome da etapa."
          >
            {renomear.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
          <Botao variante="fantasma" type="button" onClick={() => definirEditando(null)}>
            Cancelar
          </Botao>
        </span>
      </Cartao>
    );
  }

  function formularioDeRenomearFunil(funil: (typeof listaDeFunis)[number]): ReactNode {
    return (
      <Cartao
        como="form"
        variante="folgado"
        parte="admin__form"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (nomeRenomeado.trim() !== '') {
            renomearOFunil.mutate({ chave: funil.chave, nome: nomeRenomeado.trim() });
          }
        }}
      >
        <Campo
          id="renomear-funil"
          rotulo={`Nome de "${funil.nome}"`}
          type="text"
          value={nomeRenomeado}
          maxLength={40}
          autoFocus
          ajuda="Só o nome muda. As etapas e as oportunidades deste funil continuam nele."
          onChange={(e) => definirNomeRenomeado(e.currentTarget.value)}
        />

        {renomearOFunil.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(renomearOFunil.error, {
              generica: 'Não foi possível renomear o funil.',
              porCodigo: POR_CODIGO,
            })}
          </Faixa>
        )}

        <span className="admin__form-acoes">
          <Botao
            type="submit"
            variante="primario"
            desabilitado={nomeRenomeado.trim() === '' || renomearOFunil.isPending}
            motivo="Escreva o nome do funil."
          >
            {renomearOFunil.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
          <Botao variante="fantasma" type="button" onClick={() => definirRenomeandoFunil(null)}>
            Cancelar
          </Botao>
        </span>
      </Cartao>
    );
  }

  return (
    <section className="admin__secao" aria-label="Etapas do funil">
      <p className="admin__nota">
        As colunas do quadro, na ordem em que aparecem. O funil que vem de fábrica é genérico, aqui
        você personaliza o seu funil.
      </p>

      <Segmentado<'organizar' | 'criar' | 'roteamento' | 'motivos'>
        rotulo="O que fazer com os funis"
        opcoes={[
          { valor: 'organizar', rotulo: 'Funis e etapas' },
          { valor: 'criar', rotulo: 'Criar' },
          { valor: 'roteamento', rotulo: 'Roteamento' },
          { valor: 'motivos', rotulo: 'Motivos de perda' },
        ]}
        escolhida={aba}
        aoEscolher={(valor) => {
          definirAba(valor);
          // O que estava aberto era da outra aba.
          definirEditando(null);
          definirRenomeandoFunil(null);
        }}
      />

      {etapas.isPending && <p>Buscando as etapas…</p>}
      {etapas.isError && <Faixa tom="bloqueado">Não foi possível buscar as etapas.</Faixa>}

      {aba === 'organizar' ? (
        <div role="tabpanel" aria-label="Funis e etapas" className="admin__funis-painel">
          {/*
            Todos os funis de uma vez, cada um com as etapas dele.

            Antes era um de cada vez, atrás de um seletor: quem tinha "Vendas"
            e "Pós-venda" não via os dois lado a lado, e renomear a etapa do
            funil errado ficava a uma escolha de menu de distância.
          */}
          {grupos.map(({ funil, etapas: doFunil }) => (
            <section key={funil.chave} className="admin__funil" aria-label={`Funil ${funil.nome}`}>
              <header className="admin__funil-cabeca">
                <h3 className="admin__subtitulo">
                  {funil.nome}
                  <span className="admin__funil-contagem">
                    {doFunil.length === 1 ? '1 etapa' : `${doFunil.length} etapas`}
                  </span>
                </h3>
                <span className="admin__funis-acoes">
                  <Botao
                    miudo
                    desabilitado={renomearOFunil.isPending}
                    motivo="Salvando…"
                    aria-label={`Renomear o funil ${funil.nome}`}
                    onClick={() => {
                      definirNomeRenomeado(funil.nome);
                      definirRenomeandoFunil((atual) =>
                        atual === funil.chave ? null : funil.chave,
                      );
                    }}
                  >
                    Renomear
                  </Botao>
                </span>
              </header>

              {renomeandoFunil === funil.chave && formularioDeRenomearFunil(funil)}

              <ol className="admin__etapas">
                {doFunil.map((etapa, i) => linhaDaEtapa(etapa, i, doFunil))}
              </ol>

              {emEdicao && emEdicao.funil === funil.chave && formularioDeEdicao(emEdicao)}
            </section>
          ))}

          {/*
            O MOTIVO, e não "não deu": a API explica com cuidado, "esta etapa
            tem 3 oportunidades dentro".
          */}
          {remover.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(remover.error, {
                generica: 'Não foi possível remover a etapa.',
                porCodigo: POR_CODIGO,
              })}
            </Faixa>
          )}
          {mover.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(mover.error, {
                generica: 'Não foi possível reordenar.',
                porCodigo: POR_CODIGO,
              })}
            </Faixa>
          )}

          <p className="admin__nota">
            Só dá para remover etapa vazia. Com oportunidades dentro, não é possível remover, apagar
            levaria o valor junto, e cada oportunidade que estava ali sumiria do quadro sem erro
            nenhum.
          </p>
        </div>
      ) : aba === 'criar' ? (
        <div role="tabpanel" aria-label="Criar" className="admin__funis-painel">
          <Cartao
            como="form"
            variante="folgado"
            parte="admin__form"
            aria-label="Novo funil"
            onSubmit={(evento: FormEvent) => {
              evento.preventDefault();
              if (podeCriarFunil) {
                criarOFunil.mutate({
                  chave: chaveDoFunilNovo,
                  nome: nomeDoFunilNovo.trim(),
                  valor: valorDaPrimeira,
                  rotulo: rotuloDaPrimeira.trim(),
                  cor: corDaPrimeira,
                });
              }
            }}
          >
            <h3 className="admin__subtitulo">Novo funil</h3>
            {/*
              O formulário pede DUAS coisas, e a nota diz por quê antes de a
              pessoa se perguntar. Funil sem etapa não existe: ele é o conjunto
              das etapas com o prefixo dele.
            */}
            <p className="admin__nota">
              Um funil é o conjunto das etapas dele, então ele nasce com a primeira. As outras você
              acrescenta logo abaixo, em Nova etapa.
            </p>

            <Campo
              id="novo-funil-nome"
              rotulo="Nome do funil"
              type="text"
              value={nomeDoFunilNovo}
              maxLength={40}
              placeholder="Pós-venda"
              onChange={(e) => definirNomeDoFunilNovo(e.currentTarget.value)}
              erro={funilRepetido ? 'Já existe um funil com este nome.' : undefined}
            />

            <Campo
              id="novo-funil-primeira"
              rotulo="Primeira etapa"
              type="text"
              value={rotuloDaPrimeira}
              maxLength={40}
              placeholder="Orçamento"
              onChange={(e) => definirRotuloDaPrimeira(e.currentTarget.value)}
            />

            <CampoSelecao
              rotulo="Cor da primeira etapa"
              value={corDaPrimeira}
              onChange={(e) => definirCorDaPrimeira(e.currentTarget.value)}
            >
              {CORES_DE_ETAPA.map((c) => (
                <option key={c} value={c}>
                  {NOME_DA_COR[c] ?? c}
                </option>
              ))}
            </CampoSelecao>

            {criarOFunil.isError && (
              <Faixa tom="bloqueado">
                {recadoDeErro(criarOFunil.error, {
                  generica: 'Não foi possível criar o funil.',
                  porCodigo: POR_CODIGO,
                })}
              </Faixa>
            )}
            {criarOFunil.isSuccess && criarOFunil.variables && (
              <p className="admin__nota admin__nota--ok" role="status">
                Funil <strong>{criarOFunil.variables.nome}</strong> criado. Ele já está escolhido em
                Nova etapa, logo abaixo.
              </p>
            )}

            <span className="admin__form-acoes">
              <Botao
                type="submit"
                variante="primario"
                desabilitado={!podeCriarFunil}
                motivo={
                  criarOFunil.isPending
                    ? 'Criando…'
                    : 'Escreva o nome do funil e o da primeira etapa.'
                }
              >
                {criarOFunil.isPending ? 'Criando…' : 'Criar funil'}
              </Botao>
            </span>
          </Cartao>

          <Cartao
            como="form"
            variante="folgado"
            parte="admin__form"
            aria-label="Nova etapa"
            onSubmit={(evento: FormEvent) => {
              evento.preventDefault();
              if (podeCriar) criar.mutate();
            }}
          >
            <h3 className="admin__subtitulo">Nova etapa</h3>

            <CampoSelecao
              rotulo="Em qual funil"
              value={escolhido ?? ''}
              disabled={listaDeFunis.length === 0}
              onChange={(e) => {
                definirFunilEscolhido(e.currentTarget.value);
                // "Antes de Proposta" era do funil de antes.
                definirPosicao(-1);
              }}
            >
              {listaDeFunis.length === 0 && <option value="">Nenhum funil ainda</option>}
              {listaDeFunis.map((f) => (
                <option key={f.chave} value={f.chave}>
                  {f.nome}
                </option>
              ))}
            </CampoSelecao>

            <Campo
              id="nova-etapa-nome"
              rotulo="Nome da etapa"
              type="text"
              value={rotulo}
              maxLength={40}
              placeholder="Avaliação"
              onChange={(e) => definirRotulo(e.currentTarget.value)}
              erro={
                jaExiste
                  ? 'Já existe uma etapa com este nome neste funil.'
                  : valorInvalido
                    ? 'Use letras e números, começando por uma letra.'
                    : undefined
              }
            />

            {/*
              Onde ela entra. O padrão é o fim, que é o caso comum; mas um funil
              cresce pelo meio, e criar no fim para subir com as setas é
              trabalho que a tela pode fazer sozinha.
            */}
            <CampoSelecao
              rotulo="Onde entra"
              value={String(posicao)}
              onChange={(e) => definirPosicao(Number(e.currentTarget.value))}
            >
              <option value="-1">No fim</option>
              {itens.map((etapa, i) => (
                <option key={etapa.valor} value={String(i)}>
                  Antes de {nomeDaEtapa(etapa.rotulo)}
                </option>
              ))}
            </CampoSelecao>

            <CampoSelecao
              rotulo="Cor"
              value={cor}
              onChange={(e) => definirCor(e.currentTarget.value)}
            >
              {CORES_DE_ETAPA.map((c) => (
                <option key={c} value={c}>
                  {NOME_DA_COR[c] ?? c}
                </option>
              ))}
            </CampoSelecao>

            <CampoSelecao
              rotulo="Tipo da etapa"
              value={tipoNovo}
              onChange={(e) =>
                definirTipoNovo(e.currentTarget.value as Exclude<TipoDaEtapa, 'final'>)
              }
            >
              <option value="aberta">De trabalho</option>
              <option value="ganho">Final: venda ganha</option>
              <option value="perdido">Final: venda perdida</option>
            </CampoSelecao>
            <TiposDaEtapa escolhido={tipoNovo} />

            {tipoNaoGravado && (
              <Faixa tom="atencao">
                A etapa “{tipoNaoGravado}” foi criada, mas o tipo não foi gravado. Em “Funis e
                etapas”, clique em “Editar” nela e escolha o tipo.
              </Faixa>
            )}
            {criar.isError && (
              <Faixa tom="bloqueado">
                {recadoDeErro(criar.error, {
                  generica: 'Não foi possível criar a etapa.',
                  porCodigo: POR_CODIGO,
                })}
              </Faixa>
            )}
            {criar.isSuccess && ultimaCriada && (
              <p className="admin__nota admin__nota--ok" role="status">
                Etapa <strong>{ultimaCriada}</strong> acrescentada ao funil{' '}
                <strong>{funilAtual?.nome ?? ''}</strong>.
              </p>
            )}

            <span className="admin__form-acoes">
              <Botao
                type="submit"
                variante="primario"
                desabilitado={!podeCriar}
                motivo={criar.isPending ? 'Criando…' : 'Escreva o nome da etapa.'}
              >
                {/*
                  O botão diz ONDE vai entrar, e não só "criar": é o último texto
                  que a pessoa lê antes de escrever no funil de todo mundo.
                */}
                {criar.isPending
                  ? 'Criando…'
                  : posicao < 0
                    ? 'Acrescentar no fim'
                    : `Inserir antes de ${nomeDaEtapa(itens[posicao]?.rotulo) || 'a próxima'}`}
              </Botao>
            </span>
          </Cartao>
        </div>
      ) : aba === 'roteamento' ? (
        <RoteamentoDeFunil funis={funis.data?.itens ?? []} etapas={etapas.data?.itens ?? []} />
      ) : (
        <MotivosDePerdaDoFunil />
      )}
    </section>
  );
}

/**
 * O que cada tipo de etapa faz, ao lado da escolha (28/10/2026).
 *
 * O tipo decide muito mais do que o selo na coluna: se o card ainda anda, se
 * as automações o levam adiante ou abrem outro, se ele pede motivo e como os
 * relatórios o contam. Errar o tipo quebra os fluxos em silêncio — a final
 * marcada de trabalho deixa o card aberto, e a de trabalho marcada final fecha
 * o card cedo. Por isso a explicação fica à vista, e não num "saiba mais".
 */
const O_QUE_CADA_TIPO_FAZ: readonly {
  readonly tipo: Exclude<TipoDaEtapa, 'final'>;
  readonly nome: string;
  readonly faz: string;
}[] = [
  {
    tipo: 'aberta',
    nome: 'De trabalho',
    faz:
      'Um passo do caminho. A oportunidade passa por ela e segue adiante: continua em aberto, ' +
      'as automações podem levá-la à etapa seguinte, ela aparece para agendar e conta como ' +
      'negócio em andamento.',
  },
  {
    tipo: 'ganho',
    nome: 'Final: venda ganha',
    faz:
      'O objetivo do funil foi atingido. A oportunidade fecha: não se arrasta para fora, ' +
      'nenhuma automação a move, o próximo pedido do mesmo contato abre uma nova, e ela conta ' +
      'como ganho nos relatórios.',
  },
  {
    tipo: 'perdido',
    nome: 'Final: venda perdida',
    faz:
      'O contato saiu do funil. A oportunidade fecha como na ganha, conta como perda nos ' +
      'relatórios e pede o motivo da perda, quando há motivos cadastrados.',
  },
];

function TiposDaEtapa({ escolhido }: { escolhido: TipoDaEtapa }): ReactNode {
  return (
    <div className="admin__tipos">
      <dl className="admin__tipos-lista">
        {O_QUE_CADA_TIPO_FAZ.map((t) => (
          <div
            key={t.tipo}
            className={`admin__tipo${t.tipo === escolhido ? ' admin__tipo--escolhido' : ''}`}
            {...(t.tipo === escolhido ? { 'aria-current': 'true' as const } : {})}
          >
            <dt className="admin__tipo-nome">{t.nome}</dt>
            <dd className="admin__tipo-faz">{t.faz}</dd>
          </div>
        ))}
      </dl>
      <p className="admin__ajuda">
        Escolha pelo que a etapa significa: uma etapa final marcada como de trabalho deixa a
        oportunidade aberta, e as automações podem tirá-la de lá; uma de trabalho marcada como final
        fecha a oportunidade cedo, e o passo seguinte abre outra.
      </p>
    </div>
  );
}

/**
 * O rótulo virado valor: `Test drive` → `TEST_DRIVE`.
 *
 * Acento sai, porque o valor é uma chave e chave com acento vira problema de
 * codificação em algum ponto da cadeia — e esse ponto costuma ser o filtro de
 * uma consulta, onde o sintoma é lista vazia sem erro.
 *
 * ── Começar por LETRA não é capricho nosso ────────────────────────────────
 *
 * Medido contra o Twenty da staging: `1_CONTATO` volta
 * `METADATA_VALIDATION_ERROR / INVALID_FIELD_INPUT`. O motor recusa chave que
 * começa com dígito — e "1º contato", "2ª visita" são exatamente o funil de uma
 * revenda, então esse era o caminho mais provável de alguém tentar.
 *
 * O que acontecia: a tela derivava `1_CONTATO`, mostrava isso como se
 * estivesse bem, mandava, e o servidor recusava com "Requisição inválida". O
 * prefixo resolve, e ele aparece na tela ANTES de criar — chave inventada em
 * silêncio seria pior que a recusa.
 *
 * ── E o corte de 40 vem antes da limpeza, não depois ──────────────────────
 *
 * `slice` no fim podia deixar um sublinhado pendurado — `..._PARA_` —, e a
 * regra exige que todo sublinhado seja seguido de algo. Rótulo longo é comum,
 * e o sintoma era o mesmo: recusa sem explicação.
 */
function paraValor(rotulo: string): string {
  const bruto = rotulo
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const comLetra = /^[0-9]/.test(bruto) ? `ETAPA_${bruto}` : bruto;
  return comLetra.slice(0, 40).replace(/_+$/g, '');
}

/**
 * O nome do funil virado chave: `Pós-venda` → `POS_VENDA`.
 *
 * Mesma derivação da etapa, e o mesmo motivo: a chave vira o PREFIXO do valor
 * de cada etapa do funil, e o contrato do valor é `A-Z`, dígito e sublinhado.
 * É exatamente por isso que o nome existe separado — "Pós-venda" é o que se lê,
 * `POS_VENDA` é o que o motor aceita.
 *
 * O corte é em 30, e não em 40 como o da etapa: o contrato reserva o resto
 * para a etapa que vem depois do separador.
 */
function paraChaveDeFunil(nome: string): string {
  // Sem sublinhado: é a forma que o servidor guarda. A conferência de repetido
  // não usa a chave, e sim o nome (`nomeComparavel`).
  return paraValor(nome).replace(/_/g, '').slice(0, 30);
}

/**
 * O nome como o servidor o compara para dizer "funil repetido": sem acento,
 * sem caixa, só letras e dígitos de qualquer alfabeto, e o nome como foi
 * escrito quando não sobra nenhum (`nomeDeFunilComparavel`, no comercial).
 *
 * A chave não serve para isso: ela não muda ao renomear, e o nome antigo
 * ficaria ocupado. Se a chave do nome já é de outro funil, o servidor dá outra.
 */
function nomeComparavel(nome: string): string {
  const letrasEDigitos = nome
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toUpperCase();
  return letrasEDigitos === '' ? nome.trim() : letrasEDigitos;
}

/** A mesma regra do contrato, para a tela não mandar o que será recusado. */
const VALOR_VALIDO = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;

/**
 * O valor da etapa sem o prefixo do funil — o pedaço que a pessoa nomeou.
 *
 * Tira o prefixo usando o `funil` que a PRÓPRIA etapa traz, e não uma cópia do
 * separador. A diferença importa: o separador é convenção do comercial, que
 * a interface não importa (§17.1), e escrevê-lo aqui criaria a segunda regra
 * que ninguém lembraria de mudar junto.
 *
 * Serve para um uso só — conferir se o nome digitado já existe NESTE funil.
 * `F_VENDAS_NOVO` e `F_OFICINA_NOVO` convivem, então comparar valores completos
 * nunca acusaria o repetido, e a pessoa só descobriria pela recusa do motor.
 */
function semPrefixoDoFunil(etapa: EtapaDoFunil): string {
  /*
   * Procura a CHAVE no valor, e não o começo dele.
   *
   * O formato é do servidor (`F_POSVENDA_REVISAO` hoje; era `POSVENDA__…`,
   * que o motor recusava). Esta tela não o conhece: ela só sabe que a chave do
   * funil está no valor, seguida de sublinhado, e o que vem depois é o nome
   * que a pessoa deu à etapa.
   */
  if (etapa.funil === 'padrao') return etapa.valor;
  const i = etapa.valor.indexOf(etapa.funil);
  if (i < 0) return etapa.valor;
  return etapa.valor.slice(i + etapa.funil.length).replace(/^_+/, '');
}

/**
 * A ordem COMPLETA com duas etapas deste funil trocadas de lugar.
 *
 * Os índices são os da lista à vista — a do funil escolhido —, e a ordem que
 * sai é a do workspace inteiro. Mandar só a lista visível faria as etapas dos
 * outros funis irem para o fim: ver o cabeçalho de `ordem-das-etapas.ts`.
 */
function trocarNaOrdem(
  todas: readonly EtapaDoFunil[],
  doFunil: readonly EtapaDoFunil[],
  de: number,
  para: number,
): readonly string[] {
  const movido = doFunil[de];
  const alvo = doFunil[para];
  const ordem = todas.map((e) => e.valor);
  if (!movido || !alvo) return ordem;
  return ordemComTroca(ordem, movido.valor, alvo.valor);
}

/**
 * Onde a etapa nova entra, na linguagem de `ordemComEtapaInserida`.
 *
 * `posicao` é índice na lista do funil; `-1` é "no fim" — do FUNIL, e não da
 * lista inteira, por isso vira "depois da última deste funil". Funil vazio não
 * tem referência nenhuma, e aí o fim da lista é o destino certo.
 */
function alvoDaInsercao(
  doFunil: readonly EtapaDoFunil[],
  posicao: number,
): { readonly antesDe: string } | { readonly depoisDe: string } | null {
  if (posicao >= 0) {
    const antes = doFunil[posicao];
    return antes ? { antesDe: antes.valor } : null;
  }
  const ultima = doFunil[doFunil.length - 1];
  return ultima ? { depoisDe: ultima.valor } : null;
}
